/**
 * EventBus - Central event system for decoupled communication
 */

// Event type definitions
export type GameEvent =
  | { type: 'PLAYER_DAMAGED'; damage: number; currentHealth: number }
  | { type: 'PLAYER_HEALED'; amount: number; currentHealth: number }
  | { type: 'PLAYER_DIED' }
  | { type: 'ENEMY_KILLED'; enemyType: string; position: { x: number; y: number }; experienceAwarded: number }
  | { type: 'ITEM_PICKED_UP'; itemId: string; quantity: number }
  | { type: 'ITEM_CRAFTED'; recipeId: string; itemId: string }
  | { type: 'RESOURCE_GATHERED'; resourceType: string; amount: number }
  | { type: 'STRUCTURE_PLACED'; structureId: string; blueprintId: string; position: { x: number; y: number } }
  | { type: 'STRUCTURE_DESTROYED'; structureId: string }
  | { type: 'DAY_STARTED'; day: number }
  | { type: 'NIGHT_STARTED'; day: number }
  | { type: 'HOUR_PASSED'; hour: number; day: number };

export type GameEventType = GameEvent['type'];

// Extract specific event type
export type ExtractEvent<T extends GameEventType> = Extract<GameEvent, { type: T }>;

type EventCallback<T extends GameEvent> = (event: T) => void;

class EventBusClass {
  private listeners: Map<string, ((event: GameEvent) => void)[]> = new Map();
  private eventQueue: GameEvent[] = [];
  private processing: boolean = false;

  /** Subscribe to an event type */
  subscribe<T extends GameEventType>(
    eventType: T,
    callback: EventCallback<ExtractEvent<T>>
  ): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }

    const callbacks = this.listeners.get(eventType)!;
    callbacks.push(callback as (event: GameEvent) => void);

    // Return unsubscribe function
    return () => {
      const index = callbacks.indexOf(callback as (event: GameEvent) => void);
      if (index >= 0) {
        callbacks.splice(index, 1);
      }
    };
  }

  /** Publish an event (queued) */
  publish<T extends GameEvent>(event: T): void {
    this.eventQueue.push(event);
  }

  /** Publish an event immediately */
  publishImmediate<T extends GameEvent>(event: T): void {
    const callbacks = this.listeners.get(event.type);
    if (callbacks) {
      for (const callback of callbacks) {
        try {
          callback(event);
        } catch (e) {
          console.error(`Error in event handler for ${event.type}:`, e);
        }
      }
    }
  }

  /** Process all queued events */
  processQueue(): void {
    if (this.processing) return;
    this.processing = true;

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()!;
      this.publishImmediate(event);
    }

    this.processing = false;
  }

  /** Check if there are listeners for an event type */
  hasListeners(eventType: GameEventType): boolean {
    const callbacks = this.listeners.get(eventType);
    return callbacks !== undefined && callbacks.length > 0;
  }

  /** Remove all listeners */
  clear(): void {
    this.listeners.clear();
    this.eventQueue = [];
  }

  /** Get listener count for debugging */
  getListenerCount(eventType?: GameEventType): number {
    if (eventType) {
      return this.listeners.get(eventType)?.length ?? 0;
    }
    let total = 0;
    for (const callbacks of this.listeners.values()) {
      total += callbacks.length;
    }
    return total;
  }
}

// Singleton instance
export const EventBus = new EventBusClass();
